import React, { useEffect, useMemo, useRef, useState } from 'react';
import './pickupLocationBox.scss';
import { usePickupLocations } from '../../../hooks/pickupLocations';
import { ReactComponent as ChevronIcon } from '../../../icons/x.svg';
import WorkingHoursDisplay from '../WorkingHoursDisplay/WorkingHoursDisplay';

const PickupLocationBox = ({ selectedId, setSelectedId }) => {
  const { pickupLocations, pickupLocationsLoading } = usePickupLocations();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => pickupLocations.find((l) => l.id === selectedId) || null,
    [pickupLocations, selectedId],
  );

  // Auto-select the location flagged is_default once the list arrives, but
  // only when the parent hasn't already made a choice (e.g. re-opening the
  // drawer for an order that already has pickup_location_id saved).
  useEffect(() => {
    if (selectedId) return;
    if (!pickupLocations.length) return;
    const defaultLocation = pickupLocations.find((l) => l.is_default);
    if (defaultLocation) {
      setSelectedId(defaultLocation.id);
    }
  }, [pickupLocations, selectedId, setSelectedId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pickupLocations;
    return pickupLocations.filter((l) => {
      const name = (l.alias || '').toLowerCase();
      const addr = (l.address || '').toLowerCase();
      return name.includes(q) || addr.includes(q);
    });
  }, [pickupLocations, query]);

  // Close the dropdown when clicking outside the wrapper.
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSelect = (loc) => {
    setSelectedId(loc.id);
    setQuery('');
    setOpen(false);
  };

  const inputValue = open ? query : selected?.alias || selected?.address || '';

  if (pickupLocationsLoading) {
    return <div className="pickup-location-box loading">Loading...</div>;
  }

  if (!pickupLocations.length) {
    return (
      <div className="pickup-location-box empty">
        No pickup locations are available right now.
      </div>
    );
  }

  return (
    <div className="pickup-location-box" ref={wrapperRef}>
      <div className={`autocomplete ${open ? 'open' : ''}`}>
        <input
          type="text"
          className="ac-input"
          placeholder="Select a pickup location"
          value={inputValue}
          onFocus={() => setOpen(true)}
          disabled={pickupLocations.length <= 1}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        <button
          type="button"
          className="ac-toggle"
          disabled={pickupLocations.length <= 1}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle pickup location list"
        >
          <ChevronIcon />
        </button>

        {open && (
          <div className="ac-options">
            {filtered.length === 0 ? (
              <div className="ac-empty">No matches</div>
            ) : (
              filtered.map((loc) => (
                <button
                  type="button"
                  key={loc.id}
                  className={`ac-option ${
                    loc.id === selectedId ? 'active' : ''
                  }`}
                  onClick={() => handleSelect(loc)}
                >
                  <div className="row">
                    <span className="name">{loc.alias || 'Pickup point'}</span>
                  </div>
                  <div className="addr">{loc.address}</div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selected && !open && (
        <div className="selected-details">
          <div className="row">
            <span className="name">{selected.alias || 'Pickup point'}</span>
          </div>
          <div className="addr">{selected.address}</div>
           {selected.additional_info && (
            <div className="meta">{selected.additional_info}</div>
          )}
          {Array.isArray(selected.working_hours) &&
            selected.working_hours.length > 0 && (
              <WorkingHoursDisplay
                workingHours={selected.working_hours}
                style={{ marginTop: '6px' }}
              />
            )}
        </div>
      )}
    </div>
  );
};

export default PickupLocationBox;
